import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import ModalFeedback from '../components/ModalFeedback'
import { getApiBaseUrl } from '../utils/apiConfig'
import axios from 'axios'
import useRolePermissions from '../hooks/useRolePermissions'

const API_BASE_URL = getApiBaseUrl()

export default function JournalEntries() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const { canMutate, isViewer } = useRolePermissions()
  const [entries, setEntries] = useState([])
  const [chartOfAccounts, setChartOfAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('create') // create, view, edit
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFilter, setDateFilter] = useState('')

  // New entry form state
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    lines: [
      { id: 'line-1-initial', account_id: '', debit_amount: 0, credit_amount: 0 },
      { id: 'line-2-initial', account_id: '', debit_amount: 0, credit_amount: 0 }
    ]
  })

  useEffect(() => {
    fetchJournalEntries()
    fetchChartOfAccounts()
  }, [])

  const fetchJournalEntries = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_BASE_URL}/journal-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setEntries(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch journal entries:', error)
      setError(error.response?.data?.message || error.message || 'Failed to fetch journal entries')
    } finally {
      setLoading(false)
    }
  }

  const fetchChartOfAccounts = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_BASE_URL}/chart-of-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setChartOfAccounts(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch chart of accounts:', error)
    }
  }

  const handleCreate = () => {
    setModalType('create')
    setFormData({
      entry_date: new Date().toISOString().split('T')[0],
      reference: `JE-${Date.now().toString().slice(-6)}`,
      description: '',
      lines: [
        { account_id: '', debit_amount: 0, credit_amount: 0 },
        { account_id: '', debit_amount: 0, credit_amount: 0 }
      ]
    })
    setShowModal(true)
  }

  const handleView = (entry) => {
    setModalType('view')
    setSelectedEntry(entry)
    setShowModal(true)
  }

  const handleEdit = (entry) => {
    if (!canMutate) {
      showError('You do not have permission to edit journal entries')
      return
    }
    setModalType('edit')
    setSelectedEntry(entry)
    setFormData({
      entry_date: formatDateForInput(entry.entry_date),
      reference: entry.reference_display || `${entry.reference_type || ''}${entry.reference_id ? ` #${entry.reference_id}` : ''}` || '',
      description: entry.description || '',
      lines: entry.lines?.map((line, index) => ({
        id: line.id || `line-${entry.entry_id}-${index}`,
        account_id: line.account_id,
        debit_amount: line.debit_amount || 0,
        credit_amount: line.credit_amount || 0
      })) || []
    })
    setShowModal(true)
  }

  const handleDelete = async (entryId) => {
    if (!canMutate) {
      showError('You do not have permission to delete journal entries')
      return
    }
    if (!confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_BASE_URL}/journal-entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showSuccess('Journal entry deleted successfully')
      await fetchJournalEntries()
    } catch (error) {
      console.error('Failed to delete journal entry:', error)
      showError(error.response?.data?.message || 'Failed to delete journal entry')
    }
  }

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines]
    if (field === 'account_id') {
      newLines[index][field] = value
    } else {
      newLines[index][field] = parseFloat(value) || 0
    }
    setFormData({ ...formData, lines: newLines })
  }

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, {
        id: `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        account_id: '',
        debit_amount: 0,
        credit_amount: 0
      }]
    })
  }

  const removeLine = (index) => {
    if (formData.lines.length <= 2) {
      showError('Journal entries must have at least two lines')
      return
    }
    const newLines = formData.lines.filter((_, i) => i !== index)
    setFormData({ ...formData, lines: newLines })
  }

  const validateEntry = () => {
    // Check if all accounts are selected
    const emptyAccounts = formData.lines.filter(line => !line.account_id)
    if (emptyAccounts.length > 0) {
      showError('Please select an account for all lines')
      return false
    }

    // Check if at least one debit and one credit
    const hasDebit = formData.lines.some(line => line.debit_amount > 0)
    const hasCredit = formData.lines.some(line => line.credit_amount > 0)
    if (!hasDebit || !hasCredit) {
      showError('Journal entry must have at least one debit and one credit')
      return false
    }

    // Check if entry is balanced
    const totalDebits = formData.lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0)
    const totalCredits = formData.lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0)
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      showError(`Entry is not balanced. Debits: ${formatCurrency(totalDebits)}, Credits: ${formatCurrency(totalCredits)}`)
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateEntry()) return

    try {
      const token = localStorage.getItem('token')
      const totalDebits = formData.lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0)
      const totalCredits = formData.lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0)

      const entryData = {
        entry_date: formData.entry_date,
        reference_type: 'Manual',
        reference_id: null,
        description: formData.description,
        total_debit: totalDebits,
        total_credit: totalCredits,
        lines: formData.lines.filter(line => line.account_id && (line.debit_amount > 0 || line.credit_amount > 0))
      }

      if (modalType === 'create') {
        await axios.post(`${API_BASE_URL}/journal-entries`, entryData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showSuccess('Journal entry created successfully')
      } else if (modalType === 'edit') {
        await axios.put(`${API_BASE_URL}/journal-entries/${selectedEntry.entry_id}`, entryData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showSuccess('Journal entry updated successfully')
      }

      setShowModal(false)
      await fetchJournalEntries()
    } catch (error) {
      console.error('Failed to save journal entry:', error)
      showError(error.response?.data?.message || 'Failed to save journal entry')
    }
  }

  const showSuccess = (message) => {
    // You can implement a toast notification here
    alert(message)
  }

  const showError = (message) => {
    alert(message)
  }

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = searchTerm === '' ||
      (entry.description && entry.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.entry_number && entry.entry_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.reference_type && entry.reference_type.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesDate = !dateFilter || entry.entry_date === dateFilter
    return matchesSearch && matchesDate
  })

  const calculateEntryTotals = (entry) => {
    const sumLines = (field) =>
      entry.lines?.reduce((sum, line) => sum + (parseFloat(line[field]) || 0), 0) || 0
    const totalDebits = entry.lines?.length ? sumLines('debit_amount') : parseFloat(entry.total_debit) || 0
    const totalCredits = entry.lines?.length ? sumLines('credit_amount') : parseFloat(entry.total_credit) || 0
    return { totalDebits, totalCredits }
  }

  if (loading) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⏳</div>
        <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Loading journal entries...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchJournalEntries}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          🔄 Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>📝 Journal Entries</h1>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Manual double-entry bookkeeping transactions</p>
        </div>
        {canMutate && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            ＋ New Entry
          </button>
        )}
      </div>

      {/* Filters */}
      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="🔍 Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
              }`}
            />
          </div>
          <div>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={`rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
              }`}
            />
          </div>
        </div>
      </div>

      {/* Journal Entries Table */}
      <div className={`rounded-lg overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Total Debit</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Total Credit</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
              {filteredEntries.length > 0 ? (
                filteredEntries.map((entry, index) => {
                  const { totalDebits, totalCredits } = calculateEntryTotals(entry)
                  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01

                  return (
                    <tr
                      key={entry.entry_id ?? `entry-${index}`}
                      className={`cursor-pointer ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                      onClick={(e) => {
                        // Prevent row click if clicking on action buttons
                        if (e.target.closest('button')) return
                        handleEdit(entry)
                      }}
                    >
                      <td className="px-4 py-3 text-sm">{formatDate(entry.entry_date)}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {entry.reference_display
                          ? entry.reference_display
                          : entry.reference_type && entry.reference_id
                            ? `${entry.reference_type} #${entry.reference_id}`
                            : entry.reference_type || 'N/A'
                        }
                      </td>
                      <td className="px-4 py-3 text-sm">{entry.description || 'No description'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(totalDebits)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-red-600">{formatCurrency(totalCredits)}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        {isBalanced ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">✓ Balanced</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">⚠ Unbalanced</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <div className="flex gap-2 justify-center">
                          {canMutate && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEdit(entry)
                                }}
                                className="text-green-600 hover:text-green-800 font-medium"
                                title="Edit Entry"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDelete(entry.entry_id)
                                }}
                                className="text-red-600 hover:text-red-800 font-medium"
                                title="Delete Entry"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="text-4xl mb-2">📝</div>
                    <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>No journal entries found</p>
                    {canMutate && (
                      <button
                        onClick={handleCreate}
                        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                      >
                        Create First Entry
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Create/Edit/View */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="sticky top-0 bg-inherit p-6 border-b flex justify-between items-center z-10">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {modalType === 'view' ? '👁️ View Journal Entry' : modalType === 'create' ? '📝 New Journal Entry' : '✏️ Edit Journal Entry'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {modalType === 'view' ? (
                /* View Mode */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Entry Date</label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>{formatDate(selectedEntry.entry_date)}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Reference</label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>{selectedEntry.reference_display || selectedEntry.entry_number || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>{selectedEntry.description || 'No description'}</p>
                  </div>

                  <div className="space-y-2">
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Entry Lines</h4>
                    {selectedEntry.lines?.map((line, index) => {
                      // Find account from chartOfAccounts state using account_id
                      const account = chartOfAccounts.find(acc => acc.account_id === line.account_id)

                      // Extract account details safely
                      const accountCode = account?.account_code || line.account_id || 'N/A'
                      const accountName = account?.account_name || 'Unknown Account'
                      // Check both possible field names for the type
                      const balanceType = account?.balance_type || account?.account_type || 'N/A'

                      return (
                        <div key={`${selectedEntry.journal_entry_id}-${index}`} className={`flex justify-between items-center p-3 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                          <div className="flex-1">
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                              {accountName}
                            </p>
                            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                              {accountCode} - {balanceType}
                            </p>
                          </div>
                          <div className="flex gap-6">
                            <div className="text-right">
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Debit</p>
                              <p className="font-medium text-green-600">{formatCurrency(line.debit_amount || 0)}</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Credit</p>
                              <p className="font-medium text-red-600">{formatCurrency(line.credit_amount || 0)}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                /* Create/Edit Mode */
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Entry Date *</label>
                      <input
                        type="date"
                        value={formData.entry_date}
                        onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                        required
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Reference</label>
                      <input
                        type="text"
                        value={formData.reference}
                        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Entry Lines</h4>
                      <button
                        type="button"
                        onClick={addLine}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                      >
                        ＋ Add Line
                      </button>
                    </div>

                    <div className="space-y-2">
                      {formData.lines.map((line, index) => (
                        <div key={line.id || `line-${index}`} className={`flex gap-2 items-start p-3 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                          <div className="flex-1">
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Account *</label>
                            <select
                              value={line.account_id}
                              onChange={(e) => handleLineChange(index, 'account_id', e.target.value)}
                              required
                              className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDark ? 'border-slate-600 bg-slate-600 text-white' : 'border-gray-300 text-gray-700'
                              }`}
                            >
                              <option value="">Select Account</option>
                              {chartOfAccounts.map(account => (
                                <option key={account.account_id} value={account.account_id}>
                                  {account.account_code} - {account.account_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="w-24">
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Debit</label>
                            <input
                              type="number"
                              value={line.debit_amount || ''}
                              onChange={(e) => handleLineChange(index, 'debit_amount', e.target.value)}
                              min="0"
                              step="0.01"
                              className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDark ? 'border-slate-600 bg-slate-600 text-white' : 'border-gray-300 text-gray-700'
                              }`}
                            />
                          </div>
                          <div className="w-24">
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Credit</label>
                            <input
                              type="number"
                              value={line.credit_amount || ''}
                              onChange={(e) => handleLineChange(index, 'credit_amount', e.target.value)}
                              min="0"
                              step="0.01"
                              className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDark ? 'border-slate-600 bg-slate-600 text-white' : 'border-gray-300 text-gray-700'
                              }`}
                            />
                          </div>
                          <div className="pt-5">
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="text-red-600 hover:text-red-800 text-xl"
                              title="Remove Line"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Entry Summary */}
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Debits</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(formData.lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0))}
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Credits</p>
                        <p className="text-xl font-bold text-red-600">
                          {formatCurrency(formData.lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0))}
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Status</p>
                        {(() => {
                          const totalDebits = formData.lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0)
                          const totalCredits = formData.lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0)
                          const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01
                          return (
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                              isBalanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {isBalanced ? '✓ Balanced' : '⚠ Unbalanced'}
                            </span>
                          )
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border rounded-lg font-medium transition hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!canMutate}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                    >
                      {modalType === 'create' ? 'Create Entry' : 'Update Entry'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}